import React, { useState, useCallback } from 'react';
import { Node } from 'reactflow';
import { X, Trash2, Check, Loader2, Tag, Plus, Upload, Smile, Settings } from 'lucide-react';
import { uploadMediaFile } from '../../lib/storage';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';

const FIELD_OPTIONS = [
  { value: 'message', label: 'Mensaje recibido', type: 'conversation' },
  { value: 'name', label: 'Nombre del contacto', type: 'contact' },
  { value: 'tags', label: 'Etiquetas del contacto', type: 'contact' },
  { value: 'lastResponse', label: 'Última respuesta', type: 'variable' },
  { value: 'messageCount', label: 'Cantidad de mensajes', type: 'conversation' },
  { value: 'lastInteraction', label: 'Última interacción', type: 'conversation' }
];

interface NodeSidebarProps {
  node: Node;
  formData: any;
  setFormData: (data: any) => void;
  onClose: () => void;
  onSave: () => void;
  onDelete: () => void;
  isSaving: boolean;
  allNodes: Node[];
  editingOptionId?: string | null;
  setEditingOptionId?: (id: string | null) => void;
}

export const NodeSidebar: React.FC<NodeSidebarProps> = ({
  node,
  formData,
  setFormData,
  onClose,
  onSave,
  onDelete,
  isSaving,
  allNodes,
  editingOptionId,
  setEditingOptionId
}) => {
  const [newTag, setNewTag] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [showNewOption, setShowNewOption] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [currentTextArea, setCurrentTextArea] = useState<'message' | 'question' | null>(null);
  const [editingOption, setEditingOption] = useState<any>(null);
  const [newOption, setNewOption] = useState({
    name: '',
    condition: {
      type: 'conversation',
      field: '',
      operator: 'equals',
      value: ''
    }
  });

  const handleAddTag = () => {
    if (newTag.trim() && (!formData.tags || !formData.tags.includes(newTag.trim()))) {
      setFormData({
        ...formData,
        tags: [...(formData.tags || []), newTag.trim()]
      });
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData({
      ...formData,
      tags: (formData.tags || []).filter((tag: string) => tag !== tagToRemove)
    });
  };

  const handleAddKeyword = () => {
    if (newKeyword.trim() && (!formData.keywords || !formData.keywords.includes(newKeyword.trim()))) {
      setFormData({
        ...formData,
        keywords: [...(formData.keywords || []), newKeyword.trim()]
      });
      setNewKeyword('');
    }
  };

  const handleRemoveKeyword = (keywordToRemove: string) => {
    setFormData({
      ...formData,
      keywords: (formData.keywords || []).filter((keyword: string) => keyword !== keywordToRemove)
    });
  };

  const handleAddOption = () => {
    if (newOption.name && newOption.condition.field && newOption.condition.value) {
      setFormData({
        ...formData,
        options: [
          ...(formData.options || []),
          {
            id: `option-${Date.now()}`,
            ...newOption
          }
        ]
      });
      setNewOption({
        name: '',
        condition: {
          type: 'conversation',
          field: '',
          operator: 'equals',
          value: ''
        }
      });
      setShowNewOption(false);
    }
  };

  const handleEditOption = (option: any) => {
    setEditingOption(option);
    setNewOption({
      name: option.name,
      condition: option.condition
    });
    setShowNewOption(true);
  };

  const handleUpdateOption = () => {
    if (editingOption && newOption.name && newOption.condition.field && newOption.condition.value) {
      setFormData({
        ...formData,
        options: (formData.options || []).map((opt: any) =>
          opt.id === editingOption.id ? { ...opt, ...newOption } : opt
        )
      });
      setNewOption({
        name: '',
        condition: {
          type: 'conversation',
          field: '',
          operator: 'equals',
          value: ''
        }
      });
      setEditingOption(null);
      setShowNewOption(false);
    }
  };

  const handleRemoveOption = (optionId: string) => {
    setFormData({
      ...formData,
      options: (formData.options || []).filter((opt: any) => opt.id !== optionId)
    });
    if (setEditingOptionId) {
      setEditingOptionId(null);
    }
  };

  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);

      let mediaType: 'image' | 'audio' | 'document';
      switch (formData.messageType) {
        case 'image':
          mediaType = 'image';
          break;
        case 'audio':
          mediaType = 'audio';
          break;
        default:
          mediaType = 'document';
      }

      const url = await uploadMediaFile(file, mediaType);
      setFormData({
        ...formData,
        mediaUrl: url
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      alert(error instanceof Error ? error.message : 'Error al subir el archivo. Por favor, intente nuevamente.');
    } finally {
      setUploading(false);
    }
  }, [formData.messageType, setFormData]);

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    if (!currentTextArea) return;

    const field = currentTextArea === 'message' ? 'message' : 'question';
    const currentText = formData[field] || '';
    const newText = currentText + emojiData.emoji;
    
    setFormData({
      ...formData,
      [field]: newText
    });
  };

  const renderNodeSpecificFields = () => {
    switch (node.type) {
      case 'branch':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-700">Opciones</h3>
              <button
                type="button"
                onClick={() => {
                  setEditingOption(null);
                  setShowNewOption(true);
                }}
                className="inline-flex items-center px-2 py-1 text-sm text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
              >
                <Plus className="h-4 w-4 mr-1" />
                Agregar opción
              </button>
            </div>

            {/* Lista de opciones existentes */}
            <div className="space-y-3">
              {formData.options?.map((option: any) => (
                <div key={option.id} className="bg-gray-50 rounded-lg p-3 border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-sm">{option.name}</span>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => handleEditOption(option)}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        <Settings className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveOption(option.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>Campo: {FIELD_OPTIONS.find(f => f.value === option.condition.field)?.label || option.condition.field}</p>
                    <p>Operador: {option.condition.operator}</p>
                    <p>Valor: {option.condition.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Formulario para nueva opción o edición */}
            {showNewOption && (
              <div className="bg-white rounded-lg p-4 border">
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre de la opción
                    </label>
                    <input
                      type="text"
                      value={newOption.name}
                      onChange={(e) => setNewOption({ ...newOption, name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md"
                      placeholder="Ej: Si es mayor de edad"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Campo a evaluar
                    </label>
                    <select
                      value={newOption.condition.field}
                      onChange={(e) => setNewOption({
                        ...newOption,
                        condition: {
                          ...newOption.condition,
                          type: FIELD_OPTIONS.find(f => f.value === e.target.value)?.type || 'conversation',
                          field: e.target.value
                        }
                      })}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      <option value="">Selecciona un campo</option>
                      {FIELD_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Operador
                    </label>
                    <select
                      value={newOption.condition.operator}
                      onChange={(e) => setNewOption({
                        ...newOption,
                        condition: { ...newOption.condition, operator: e.target.value }
                      })}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      <option value="equals">Es igual a</option>
                      <option value="contains">Contiene</option>
                      <option value="startsWith">Empieza con</option>
                      <option value="endsWith">Termina con</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valor
                    </label>
                    <input
                      type="text"
                      value={newOption.condition.value}
                      onChange={(e) => setNewOption({
                        ...newOption,
                        condition: { ...newOption.condition, value: e.target.value }
                      })}
                      className="w-full px-3 py-2 border rounded-md"
                      placeholder="Ej: 18"
                    />
                  </div>
                  <div className="flex justify-end space-x-2 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewOption(false);
                        setEditingOption(null);
                        setNewOption({
                          name: '',
                          condition: {
                            type: 'conversation',
                            field: '',
                            operator: 'equals',
                            value: ''
                          }
                        });
                      }}
                      className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={editingOption ? handleUpdateOption : handleAddOption}
                      className="px-3 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
                      disabled={!newOption.name || !newOption.condition.field || !newOption.condition.value}
                    >
                      {editingOption ? 'Actualizar opción' : 'Agregar opción'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'sendMessage':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Mensaje
              </label>
              <select
                value={formData.messageType || 'text'}
                onChange={(e) => setFormData({ ...formData, messageType: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="text">Texto</option>
                <option value="image">Imagen</option>
                <option value="audio">Audio</option>
                <option value="document">Documento</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contenido del Mensaje
              </label>
              <div className="relative">
                <textarea
                  value={formData.message || ''}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border rounded-md pr-10"
                  placeholder="Escribe tu mensaje aquí..."
                />
                <button
                  type="button"
                  onClick={() => {
                    setCurrentTextArea('message');
                    setShowEmojiPicker(!showEmojiPicker);
                  }}
                  className="absolute right-2 top-2 text-gray-500 hover:text-gray-700"
                >
                  <Smile className="h-5 w-5" />
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Puedes usar *texto* para negrita, _texto_ para cursiva y ~texto~ para tachado.
              </p>
            </div>
            {['image', 'audio', 'document'].includes(formData.messageType || '') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Archivo {formData.messageType === 'image' ? 'de imagen' : 
                          formData.messageType === 'audio' ? 'de audio' : 
                          'adjunto'}
                </label>
                {formData.mediaUrl ? (
                  <div className="flex items-center justify-between p-2 border rounded-md">
                    <div className="text-sm text-gray-600 truncate flex-1">
                      {formData.mediaUrl}
                    </div>
                    <label className="cursor-pointer ml-2">
                      <input
                        type="file"
                        className="hidden"
                        onChange={handleFileUpload}
                        accept={
                          formData.messageType === 'image' ? 'image/*' :
                          formData.messageType === 'audio' ? 'audio/*' :
                          '*/*'
                        }
                      />
                      {uploading ? (
                        <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                      ) : (
                        <Upload className="h-5 w-5 text-blue-500 hover:text-blue-600" />
                      )}
                    </label>
                  </div>
                ) : (
                  <label className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-blue-500 transition-colors">
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleFileUpload}
                      accept={
                        formData.messageType === 'image' ? 'image/*' :
                        formData.messageType === 'audio' ? 'audio/*' :
                        '*/*'
                      }
                    />
                    {uploading ? (
                      <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                    ) : (
                      <div className="text-center">
                        <Upload className="h-6 w-6 mx-auto text-gray-400" />
                        <span className="mt-2 block text-sm font-medium text-gray-900">
                          Subir archivo
                        </span>
                      </div>
                    )}
                  </label>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {formData.messageType === 'image' ? 'Formatos soportados: JPG, PNG, GIF' :
                   formData.messageType === 'audio' ? 'Formatos soportados: MP3, WAV, OGG' :
                   'Formatos soportados: PDF, DOC, DOCX, XLS, XLSX'}
                </p>
              </div>
            )}
          </>
        );

      case 'askQuestion':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pregunta
              </label>
              <div className="relative">
                <textarea
                  value={formData.question || ''}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border rounded-md pr-10"
                  placeholder="Escribe tu pregunta aquí..."
                />
                <button
                  type="button"
                  onClick={() => {
                    setCurrentTextArea('question');
                    setShowEmojiPicker(!showEmojiPicker);
                  }}
                  className="absolute right-2 top-2 text-gray-500 hover:text-gray-700"
                >
                  <Smile className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Variable para guardar respuesta
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-3 py-2 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                  $
                </span>
                <input
                  type="text"
                  value={formData.variable || ''}
                  onChange={(e) => setFormData({ ...formData, variable: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md"
                  placeholder="nombre_variable"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Esta variable guardará la respuesta del usuario
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tiempo de espera (minutos)
              </label>
              <input
                type="number"
                min="1"
                max="60"
                value={formData.timeout || 1}
                onChange={(e) => setFormData({ ...formData, timeout: parseInt(e.target.value, 10) || 1 })}
                className="w-full px-3 py-2 border rounded-md"
              />
              <p className="mt-1 text-xs text-gray-500">
                Tiempo máximo de espera para recibir una respuesta
              </p>
            </div>
          </>
        );

      case 'waitForResponse':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tiempo de espera (segundos)
            </label>
            <input
              type="number"
              value={formData.timeout || ''}
              onChange={(e) => setFormData({ ...formData, timeout: e.target.value })}
              className="w-full px-3 py-2 border rounded-md"
            />
            <p className="mt-1 text-xs text-gray-500">
              Tiempo máximo de espera antes de continuar
            </p>
          </div>
        );

      case 'httpRequest':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                URL
              </label>
              <input
                type="text"
                value={formData.url || ''}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
                placeholder="https://api.ejemplo.com/endpoint"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Método
              </label>
              <select
                value={formData.method || 'GET'}
                onChange={(e) => setFormData({ ...formData, method: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Variable para guardar respuesta
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-3 py-2 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                  $
                </span>
                <input
                  type="text"
                  value={formData.responseVariable || ''}
                  onChange={(e) => setFormData({ ...formData, responseVariable: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md"
                  placeholder="respuesta_api"
                />
              </div>
            </div>
          </>
        );

      case 'jump':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nodo destino
              </label>
              <select
                value={formData.targetNode || ''}
                onChange={(e) => {
                  const selectedNode = allNodes.find(n => n.id === e.target.value);
                  setFormData({
                    ...formData,
                    targetNode: e.target.value,
                    targetNodeName: selectedNode ? selectedNode.data.title || `${selectedNode.type} (${selectedNode.id})` : ''
                  });
                }}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="">Seleccionar nodo destino</option>
                {allNodes
                  .filter(n => 
                    n.id !== node.id && 
                    n.type !== 'jump' &&
                    n.type !== 'trigger'
                  )
                  .map(n => (
                    <option key={n.id} value={n.id}>
                      {n.data.title || `${n.type} (${n.id})`}
                    </option>
                  ))
                }
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Selecciona el nodo al que quieres saltar (no se puede saltar a nodos de tipo Jump o Trigger)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Límite de saltos
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={formData.jumpLimit || '3'}
                onChange={(e) => setFormData({ ...formData, jumpLimit: parseInt(e.target.value, 10) || 3 })}
                className="w-full px-3 py-2 border rounded-md"
              />
              <p className="mt-1 text-xs text-gray-500">
                Número máximo de saltos permitidos para evitar bucles infinitos
              </p>
            </div>
          </>
        );

      case 'end':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mensaje final (opcional)
            </label>
            <div className="relative">
              <textarea
                value={formData.message || ''}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border rounded-md pr-10"
                placeholder="Mensaje opcional para enviar antes de finalizar la conversación..."
              />
              <button
                type="button"
                onClick={() => {
                  setCurrentTextArea('message');
                  setShowEmojiPicker(!showEmojiPicker);
                }}
                className="absolute right-2 top-2 text-gray-500 hover:text-gray-700"
              >
                <Smile className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Este mensaje se enviará antes de finalizar la conversación
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="w-96 bg-white border-l overflow-y-auto shadow-md">
      <div className="p-4 border-b flex items-center justify-between">
        <h2 className="text-lg font-medium text-gray-900">Editar Nodo</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-500"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="p-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Título
          </label>
          <input
            type="text"
            value={formData.title || ''}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-3 py-2 border rounded-md"
            placeholder="Título del nodo"
          />
        </div>

        {renderNodeSpecificFields()}

        <div className="pt-4 space-y-2">
          <button
            onClick={onSave}
            disabled={isSaving}
            className="w-full flex items-center justify-center px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            Guardar cambios
          </button>

          <button
            onClick={onDelete}
            className="w-full flex items-center justify-center px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Eliminar nodo
          </button>
        </div>
      </div>

      {/* Emoji Picker */}
      {showEmojiPicker && (
        <div className="absolute right-96 top-0 z-50">
          <div className="bg-white rounded-lg shadow-xl border">
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              autoFocusSearch={false}
              width={320}
              height={400}
            />
          </div>
        </div>
      )}
    </div>
  );
};